import { flash, SubmitButton } from "@xola/ui-kit";
import { isExperienceProduct, SecondaryButton } from "common";
import { FormikProps } from "formik";
import { isNull, omit } from "lodash";
import React, { useRef } from "react";
import { useMutation } from "react-query";
import {
    CheckInPreference,
    CheckInValidationType,
    Experience,
    ExperienceNotification,
    Permission,
    QrCodePreference,
    UserDetailsRequirement,
} from "types";
import { ExperienceCheckInFormValues } from "../ExperienceCheckIn/ExperienceCheckIn.types";
import { AttendeesSection } from "./AttendeesSection";
import { ExperienceCheckInSection } from "./ExperienceCheckInSection";
import { ExperienceTicketsSection } from "./ExperienceTicketsSection";
import { getCheckInPreferenceValues, getInitialQrCodeValues, getValidationSchema } from "./helpers";
import { DEFAULT_NOTIFICATIONS, getNotificationsWithDefaults, isCustomized } from "./NotificationsSection";
import { PurchaserInformationSection } from "./PurchaserInformationSection";
import { api } from "@/api/api";
import { ConfirmLeaveModal } from "@/components/Common/ConfirmLeaveModal";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { sanitizeExperiencePayload } from "@/models/Experience";

interface GuestManagementProps {
    experience: Experience;
}

export type GuestManagementFormValues = ExperienceCheckInFormValues & {
    customerNamePreference: Experience["customerNamePreference"];
    requireGuestIdentification: Experience["requireGuestIdentification"];
    qrCodePreference: QrCodePreference;
    notifications: ExperienceNotification[];
    isNotificationsCustomized: boolean;
};

export const GuestManagement = ({ experience }: GuestManagementProps) => {
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const formikRef = useRef<FormikProps<GuestManagementFormValues>>(null);

    const isExperience = isExperienceProduct(experience);

    const showTicketsTab = isGranted(Permission.QRCODE_VIEW);

    const notifications = getNotificationsWithDefaults(experience.notifications);

    const initialValues: GuestManagementFormValues = {
        customerNamePreference: experience.customerNamePreference ?? UserDetailsRequirement.REQUIRED,
        requireGuestIdentification: experience.requireGuestIdentification ?? true,
        qrCodePreference: getInitialQrCodeValues(experience),
        checkInPreference: getCheckInPreferenceValues(experience.checkInPreference),
        notifications,
        isNotificationsCustomized: isCustomized(notifications),
        purchaseExpirationOffset: null,
        firstCheckinExpirationOffset: null,
    };

    const { mutateAsync: updateQRCodePreference, isLoading } = useMutation(async (payload: QrCodePreference) => {
        return api.post(`/experiences/${experience.id}/qrCode`, payload);
    });

    const { mutateAsync: updateCheckInPreference, isLoading: isSavingCheckInPreference } = useMutation(
        async (payload: CheckInPreference | null) => {
            const url = `/experiences/${experience.id}/checkIn`;
            return isNull(payload) ? api.delete(url) : api.put(url, payload);
        },
    );

    const handleSubmit = async (values: GuestManagementFormValues) => {
        if (!experience) return;

        await api.put(
            `/experiences/${experience.id}`,
            sanitizeExperiencePayload({
                ...experience,
                customerNamePreference: values.customerNamePreference,
                requireGuestIdentification: values.requireGuestIdentification,
                notifications: values.requireGuestIdentification ? values.notifications : DEFAULT_NOTIFICATIONS,
            }),
        );

        if (showTicketsTab) {
            await updateQRCodePreference(values.qrCodePreference);
        }

        if (values.checkInPreference?.enabled === false) {
            values.checkInPreference.validations = [];
        }

        if (values.checkInPreference?.checkInWindowEnabled === false) {
            values.checkInPreference.validations = values.checkInPreference.validations.filter(
                (validation) =>
                    validation.object !== CheckInValidationType.PURCHASE_CUTOFF &&
                    validation.object !== CheckInValidationType.SCHEDULE,
            );
        }

        if (values.checkInPreference?.validations) {
            values.checkInPreference.validations = values.checkInPreference.validations.map((validation) => {
                if (validation.id?.includes("draft:")) {
                    delete validation.id;
                }
                return validation;
            });
        }

        await updateCheckInPreference(
            values.checkInPreference
                ? omit(values.checkInPreference, ["multiCheckInEnabled", "checkInWindowEnabled"])
                : null,
        );

        flash.success("Successfully updated Guest Management settings");
        invalidate("experiences", experience.id);
    };

    return (
        <ContentLayout isWide>
            <XolaFormik
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={getValidationSchema()}
                onSubmit={handleSubmit}
            >
                {({ resetForm, values, setFieldValue, isSubmitting, handleSubmit }) => (
                    <>
                        <ConfirmLeaveModal />

                        <AttendeesSection experience={experience} value={values?.requireGuestIdentification ?? true} />

                        <ExperienceCheckInSection experience={experience} />

                        {showTicketsTab && (
                            <ExperienceTicketsSection
                                values={values.qrCodePreference}
                                onChange={(field, value) => setFieldValue(`qrCodePreference.${field}`, value)}
                            />
                        )}

                        {isExperience && (
                            <PurchaserInformationSection
                                customerNamePreference={
                                    values.customerNamePreference ?? UserDetailsRequirement.REQUIRED
                                }
                                onChange={(value) => setFieldValue("customerNamePreference", value)}
                            />
                        )}

                        <ContentLayout.Footer preferenceSidebar>
                            <SubmitButton
                                isLoading={isSubmitting || isLoading || isSavingCheckInPreference}
                                type="submit"
                                size="medium"
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>
                            <SecondaryButton onClick={() => resetForm()}>Cancel</SecondaryButton>
                        </ContentLayout.Footer>
                        <ScrollToFieldError />
                    </>
                )}
            </XolaFormik>
        </ContentLayout>
    );
};
